/**
 * Toast Message Normalizer and Humanizer
 * Converts raw error codes, English messages, or unformatted text into clear, understandable Turkish notifications.
 */

export function humanizeToastMessage(rawTitle, rawDescription, appearance = "info") {
  let text = (rawDescription || rawTitle || "").trim();
  let heading = (rawTitle || "").trim();

  // If text or heading contains raw error objects or error-like strings
  if (typeof text !== "string") {
    try {
      text = JSON.stringify(text);
    } catch {
      text = "Beklenmeyen bir durum oluştu.";
    }
  }

  const lower = text.toLowerCase();
  const lowerHeading = heading.toLowerCase();

  // 1. Network / Server connection errors
  if (
    lower.includes("network error") ||
    lower.includes("econnrefused") ||
    lower.includes("failed to fetch") ||
    lower.includes("net::err_")
  ) {
    heading = "Bağlantı Hatası";
    text = "Sunucuya bağlanılamadı. Lütfen internet bağlantınızı ve ağ durumunuzu kontrol ediniz.";
  }
  // 2. Authentication / Token errors
  else if (
    lower.includes("jwt expired") ||
    lower.includes("token expired") ||
    lower.includes("token is not valid") ||
    lower.includes("jwt malformed") ||
    (lower.includes("401") && lower.includes("unauthorized"))
  ) {
    heading = "Oturum Süresi Doldu";
    text = "Güvenliğiniz için oturumunuzun süresi doldu. Lütfen tekrar giriş yapınız.";
  }
  // 3. Permission / Forbidden errors
  else if (
    lower.includes("403") ||
    lower.includes("forbidden") ||
    lower.includes("yetkiniz bulunmuyor") ||
    lower.includes("access denied")
  ) {
    heading = "Yetkisiz İşlem";
    text = "Bu işlemi gerçekleştirmek veya ilgili veriyi görüntülemek için gerekli yetkiniz bulunmamaktadır.";
  }
  // 4. Not Found errors
  else if (
    lower.includes("404") ||
    lower.includes("not found") ||
    lower.includes("bulunamadı")
  ) {
    if (!heading || heading === "Hata" || heading === "UI.ERROR") {
      heading = "Kayıt Bulunamadı";
    }
    if (lower.includes("route") || lower.includes("cannot get")) {
      text = "Talep edilen sunucu servisi veya sayfa bulunamadı.";
    }
  }
  // 5. Duplicate key / Conflict errors
  else if (
    lower.includes("e11000") ||
    lower.includes("duplicate key") ||
    lower.includes("already exists") ||
    lower.includes("zaten mevcut")
  ) {
    heading = "Mükerrer Kayıt";
    text = "Girdiğiniz kod, seri numarası veya isim sistemde zaten kayıtlı. Lütfen farklı bir bilgi giriniz.";
  }
  // 6. Common English API error messages -> Clear Turkish
  else if (lower.includes("please select a dealer")) {
    heading = "Bayi Seçimi Gerekli";
    text = "Satış işlemine devam edebilmek için lütfen bir bayi veya müşteri seçiniz.";
  } else if (lower.includes("sale created successfully")) {
    heading = "Satış Kaydedildi";
    text = "Cihaz satışı ve stok dökümü başarıyla oluşturuldu.";
  } else if (lower.includes("error creating sale")) {
    heading = "Satış Kaydedilemedi";
    text = "Satış kaydı oluşturulurken bir hata oluştu. Lütfen girilen verileri kontrol ediniz.";
  } else if (lower.includes("sale updated successfully")) {
    heading = "Satış Güncellendi";
    text = "Satış kaydı ve detayları başarıyla güncellendi.";
  } else if (lower.includes("error updating sale")) {
    heading = "Güncelleme Başarısız";
    text = "Satış kaydı güncellenirken bir sorun oluştu. Lütfen tekrar deneyiniz.";
  } else if (lower.includes("failed to load data") || lower.includes("error loading")) {
    heading = "Veri Alınamadı";
    text = "Veriler sunucudan yüklenirken bir sorun oluştu. Lütfen sayfayı yenileyiniz.";
  } else if (lower.includes("delivery created successfully")) {
    heading = "Teslimat Oluşturuldu";
    text = "Yeni teslimat kaydı başarıyla oluşturuldu.";
  } else if (lower.includes("delivery updated successfully")) {
    heading = "Teslimat Güncellendi";
    text = "Teslimat detayları başarıyla kaydedildi.";
  } else if (lower.includes("company created successfully")) {
    heading = "Firma Eklendi";
    text = "Yeni firma kaydı başarıyla oluşturuldu.";
  } else if (lower.includes("error creating company")) {
    heading = "Firma Eklenemedi";
    text = "Firma kaydedilirken bir hata oluştu. Lütfen bilgileri kontrol ediniz.";
  }

  // 7. If rawTitle contains a colon (e.g. "Kritik Stok: 3 adet kaldı")
  if (!rawDescription && rawTitle && rawTitle.includes(":") && !rawTitle.includes("http")) {
    const parts = rawTitle.split(":");
    heading = parts[0].trim();
    text = parts.slice(1).join(":").trim();
  }

  // 8. Normalizing generic or translation keys in heading
  if (
    !heading ||
    lowerHeading === "ui.error" ||
    lowerHeading === "error" ||
    lowerHeading === "hata" ||
    lowerHeading === "hata!"
  ) {
    if (appearance === "danger") {
      heading = "İşlem Gerçekleştirilemedi";
    } else if (appearance === "warning") {
      heading = "Dikkat Edilmesi Gereken Durum";
    } else if (appearance === "success") {
      heading = "İşlem Başarılı";
    } else {
      heading = "Bilgilendirme";
    }
  } else if (
    lowerHeading === "ui.success" ||
    lowerHeading === "success" ||
    lowerHeading === "başarılı" ||
    lowerHeading === "başarılı!"
  ) {
    heading = "İşlem Başarıyla Tamamlandı";
  }

  // If text is still identical to heading or empty
  if (!text) {
    text = heading;
  }

  return { heading, text };
}

